/**
 * QuickStatsOverlay.ts — Modal overlay for quick character stats reference.
 *
 * Displays all player stats in a bordered box that can be dismissed with any key.
 * Used during skill checks to let players quickly review their stats without
 * navigating to the full character sheet.
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import { theme, type ColorName } from './ColorTheme';
import { calculateModifier } from './StatPeek';

export interface StatInfo {
    /** Full stat name (e.g., 'Strength') */
    name: string;
    /** Abbreviated stat name (e.g., 'STR') */
    abbreviation: string;
    /** Current stat value */
    value: number;
    /** Whether this is a primary/highlighted stat */
    isPrimary?: boolean;
}

export interface QuickStatsOverlayOptions {
    /** Stats to display */
    stats: StatInfo[];
    /** Optional: highlight a specific stat (for skill check context) */
    highlightStat?: string;
    /** Title for the overlay */
    title?: string;
}

export class QuickStatsOverlay {
    private terminal: Terminal;
    private inputManager: InputManager;
    private visible = false;

    constructor(terminal: Terminal, inputManager: InputManager) {
        this.terminal = terminal;
        this.inputManager = inputManager;
    }

    /** Check if overlay is currently visible */
    isVisible(): boolean {
        return this.visible;
    }

    /** Show the overlay and wait for any key to dismiss */
    async show(options: QuickStatsOverlayOptions): Promise<void> {
        if (this.visible) return;
        this.visible = true;

        const { stats, highlightStat, title = 'CHARACTER STATS' } = options;

        // Save current input mode
        const previousMode = this.inputManager.getMode();
        this.inputManager.setMode('anyKey');

        try {
            // Render the overlay
            this.render(stats, highlightStat, title);

            // Wait for any key
            await new Promise<void>((resolve) => {
                const handler = () => {
                    this.inputManager.off(handler);
                    resolve();
                };
                this.inputManager.on(handler);
            });
        } finally {
            // Clear the overlay and restore state
            this.clear();
            this.visible = false;
            this.inputManager.setMode(previousMode);
        }
    }

    /** Render the stats overlay in the center of the terminal */
    private render(stats: StatInfo[], highlightStat: string | undefined, title: string): void {
        const { cols, rows } = this.terminal.getSize();

        // Calculate box dimensions
        const boxWidth = Math.min(42, cols - 4);
        const boxHeight = Math.min(10, rows - 4);
        const startCol = Math.floor((cols - boxWidth) / 2) + 1;
        const startRow = Math.floor((rows - boxHeight) / 2) + 1;

        // Save cursor position
        this.terminal.writeRaw('\x1b[s');

        // Draw box border
        const topBorder = '╔' + '═'.repeat(boxWidth - 2) + '╗';
        const bottomBorder = '╚' + '═'.repeat(boxWidth - 2) + '╝';
        const midBorder = '╠' + '═'.repeat(boxWidth - 2) + '╣';
        const emptyLine = '║' + ' '.repeat(boxWidth - 2) + '║';

        // Row 1: Top border
        this.terminal.setCursor(startRow, startCol);
        this.terminal.write(topBorder, 'frostBlue');

        // Row 2: Title
        this.terminal.setCursor(startRow + 1, startCol);
        this.terminal.write('║', 'frostBlue');
        const titlePadded = title.padStart(Math.floor((boxWidth - 2 + title.length) / 2)).padEnd(boxWidth - 2);
        this.terminal.write(titlePadded, 'white');
        this.terminal.write('║', 'frostBlue');

        // Row 3: Separator
        this.terminal.setCursor(startRow + 2, startCol);
        this.terminal.write(midBorder, 'frostBlue');

        // Rows 4-6: Stats (2 per row)
        const statRows = this.formatStatsInRows(stats, highlightStat, boxWidth - 4);
        for (let i = 0; i < statRows.length && i < 3; i++) {
            this.terminal.setCursor(startRow + 3 + i, startCol);
            this.terminal.write('║ ', 'frostBlue');
            this.terminal.writeRaw(statRows[i]);
            // Pad to fill the line
            const visibleLength = this.stripAnsi(statRows[i]).length;
            const padding = boxWidth - 4 - visibleLength;
            if (padding > 0) {
                this.terminal.write(' '.repeat(padding));
            }
            this.terminal.write(' ║', 'frostBlue');
        }

        // Fill remaining rows if needed
        for (let i = statRows.length; i < 3; i++) {
            this.terminal.setCursor(startRow + 3 + i, startCol);
            this.terminal.write(emptyLine, 'frostBlue');
        }

        // Row 7: Separator
        this.terminal.setCursor(startRow + 6, startCol);
        this.terminal.write(midBorder, 'frostBlue');

        // Row 8: Instructions
        this.terminal.setCursor(startRow + 7, startCol);
        this.terminal.write('║', 'frostBlue');
        const instructions = 'Press any key to close';
        const instrPadded = instructions.padStart(Math.floor((boxWidth - 2 + instructions.length) / 2)).padEnd(boxWidth - 2);
        this.terminal.write(instrPadded, 'frostBlue');
        this.terminal.write('║', 'frostBlue');

        // Row 9: Bottom border
        this.terminal.setCursor(startRow + 8, startCol);
        this.terminal.write(bottomBorder, 'frostBlue');

        // Restore cursor position
        this.terminal.writeRaw('\x1b[u');
    }

    /** Format stats into rows (2 stats per row) */
    private formatStatsInRows(stats: StatInfo[], highlightStat: string | undefined, maxWidth: number): string[] {
        const rows: string[] = [];
        const statWidth = Math.floor(maxWidth / 2);

        for (let i = 0; i < stats.length; i += 2) {
            const left = this.formatSingleStat(stats[i], highlightStat, statWidth);
            const right = stats[i + 1] ? this.formatSingleStat(stats[i + 1], highlightStat, statWidth) : '';
            rows.push(left + right);
        }

        return rows;
    }

    /** Format a single stat with optional highlighting */
    private formatSingleStat(stat: StatInfo, highlightStat: string | undefined, width: number): string {
        const modifier = calculateModifier(stat.value);
        const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
        const isHighlighted = highlightStat &&
            (stat.abbreviation.toLowerCase() === highlightStat.toLowerCase() ||
                stat.name.toLowerCase() === highlightStat.toLowerCase());

        const color: ColorName = isHighlighted ? 'yellow' : (stat.isPrimary ? 'frostBlue' : 'white');
        const text = `${stat.abbreviation}  ${String(stat.value).padStart(2)} (${modStr})`;
        const padded = text.padEnd(width);

        return theme.colorize(padded, color);
    }

    /** Strip ANSI codes from a string to get visible length */
    private stripAnsi(str: string): string {
        return str.replace(/\x1b\[[0-9;]*m/g, '');
    }

    /** Clear the overlay area */
    private clear(): void {
        const { cols, rows } = this.terminal.getSize();
        const boxWidth = Math.min(42, cols - 4);
        const boxHeight = 9;
        const startCol = Math.floor((cols - boxWidth) / 2) + 1;
        const startRow = Math.floor((rows - boxHeight) / 2) + 1;

        // Save cursor, clear each line, restore cursor
        this.terminal.writeRaw('\x1b[s');
        for (let i = 0; i < boxHeight; i++) {
            this.terminal.setCursor(startRow + i, startCol);
            this.terminal.write(' '.repeat(boxWidth));
        }
        this.terminal.writeRaw('\x1b[u');
    }

    /**
     * Get the rendered content as an array of stat strings (for testing).
     * Returns the stats in the format they would be displayed.
     */
    getRenderedStats(stats: StatInfo[]): string[] {
        return stats.map(stat => {
            const modifier = calculateModifier(stat.value);
            const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
            return `${stat.abbreviation}: ${stat.value} (${modStr})`;
        });
    }
}
